import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserDataService from "../services/UserService";

import { debugLog, errorLog } from "../utils/debugUtils";
import { getValuesByPropertyPath } from "../utils/dataUtils";
import { getRejectedError, toRejectedPayload } from "../utils/sliceErrorUtils";

// Use per-operation status & error tracking]
export const initialState = {
	list: [],
	selectedUser: null,

	status: {
		retrieve: "idle",
		retrieveOne: "idle",
		create: "idle",
		update: "idle",
		find: "idle",
	},
	error: {
		retrieve: null,
		retrieveOne: null,
		create: null,
		update: null,
		find: null,
	},
};

// Keep User CRUD/search thunks on the same rejectWithValue contract as
// Baseline/Scenario so reducers can reliably display API-provided messages.
export const createUser = createAsyncThunk("users/create", async ({ data }, { rejectWithValue }) => {
	try {
		const res = await UserDataService.create(data);
		debugLog(3, "[userSlice][FLOW] createUser response", { response: res.data });
		return res.data;
	} catch (err) {
		errorLog(4, "[userSlice][PROCESS] createUser rejected", { error: err });
		return rejectWithValue(toRejectedPayload(err, "User create failed."));
	}
});

export const retrieveUsers = createAsyncThunk("users/retrieve", async (_, { rejectWithValue }) => {
	try {
		const res = await UserDataService.getAll();
		debugLog(3, "[userSlice][FLOW] retrieveUsers response", { response: res.data });

		let arrayUsers = [];
		res?.data?.forEach((u) => {
			let tempRoles = [];
			u.user_accesses?.forEach(function (item) {
				tempRoles.push(item.user_role);
			});
			let sortedTempRoles = tempRoles.sort((r1, r2) =>
				r1.sort_id > r2.sort_id ? 1 : r1.sort_id < r2.sort_id ? -1 : 0
			);

			arrayUsers.push({
				...u,
				user_roles: sortedTempRoles,
				user_roles_desc:
					sortedTempRoles.length > 0
						? getValuesByPropertyPath(sortedTempRoles, "name").join(", ")
						: "Basic User",
			});
		});

		debugLog(4, "[userSlice][PROCESS] retrieveUsers transformed", { arrayUsers: arrayUsers });
		return arrayUsers;
	} catch (err) {
		errorLog(4, "[userSlice][PROCESS] retrieveUsers rejected", { error: err });
		return rejectWithValue(toRejectedPayload(err, "User retrieve failed."));
	}
});

// Add selectedUser logic or a selector to get a user by ID
export const selectUserById = (state, userId) => state.users.list.find((u) => u.id === userId);

export const retrieveOneUser = createAsyncThunk("users/retrieveOne", async (id, { rejectWithValue }) => {
	try {
		const res = await UserDataService.get(id);
		debugLog(3, "[userSlice][FLOW] retrieveOneUser response", { response: res.data });

		let tempRoles = [];
		res?.data?.user_accesses?.forEach(function (item) {
			tempRoles.push(item.user_role);
		});
		const transformed = { ...res.data, user_roles: tempRoles };

		debugLog(4, "[userSlice][PROCESS] retrieveOneUser transformed", { transformed: transformed });

		return transformed;
	} catch (err) {
		errorLog(4, "[userSlice][PROCESS] retrieveOneUser rejected", { error: err });
		return rejectWithValue(toRejectedPayload(err, "User retrieve failed."));
	}
});

export const retrieveOneUserByUuid = createAsyncThunk(
	"users/retrieveOneByUuid",
	async (uuid, { rejectWithValue }) => {
		try {
			const res = await UserDataService.findByUuid(uuid);
			debugLog(3, "[userSlice][FLOW] retrieveOneUserByUuid response", { response: res.data });

			let tempRoles = [];
			res?.data?.user_accesses?.forEach(function (item) {
				tempRoles.push(item.user_role);
			});
			return { ...res.data, user_roles: tempRoles };
		} catch (err) {
			errorLog(4, "[userSlice][PROCESS] retrieveOneUserByUuid rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User retrieve failed."));
		}
	}
);

export const updateUser = createAsyncThunk("users/update", async ({ id, data }, { rejectWithValue }) => {
	try {
		const res = await UserDataService.update(id, data);
		debugLog(3, "[userSlice][FLOW] updateUser response", { response: res.data });
		return res.data;
	} catch (err) {
		errorLog(4, "[userSlice][PROCESS] updateUser rejected", { error: err });
		return rejectWithValue(toRejectedPayload(err, "User update failed."));
	}
});

export const findUsersByEmail = createAsyncThunk(
	"users/findByEmail",
	async ({ email }, { rejectWithValue }) => {
		try {
			const res = await UserDataService.findByEmail(email);
			debugLog(3, "[userSlice][FLOW] findUsersByEmail response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[userSlice][PROCESS] findUsersByEmail rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User search failed."));
		}
	}
);

export const findUsersByKeyword = createAsyncThunk(
	"users/findByKeyword",
	async ({ keyword }, { rejectWithValue }) => {
		try {
			const res = await UserDataService.findByKeyword(keyword);
			debugLog(3, "[userSlice][FLOW] findUsersByKeyword response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[userSlice][PROCESS] findUsersByKeyword rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User search failed."));
		}
	}
);

export const updateStatus = createAsyncThunk(
	"users/updateStatus",
	async ({ id, status_id, updatedBy }, { rejectWithValue }) => {
		try {
			const response = await UserDataService.updateStatus(id, status_id, updatedBy);
			debugLog(3, "[userSlice][FLOW] updateStatus response", { response: response.data });
			return { id, status_id }; // Minimal return; adjust as needed
		} catch (err) {
			errorLog(4, "[userSlice][PROCESS] updateStatus rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User status update failed."));
		}
	}
);

const userSlice = createSlice({
	name: "users",
	initialState,
	reducers: {
		setSelectedUser: (state, action) => {
			// safeguard against null/undefined/falsey payload
			state.selectedUser = action.payload ?? null;
			debugLog(4, "[userSlice][PROCESS] setSelectedUser", { payload: action.payload });
		},
		clearSelectedUser: (state) => {
			state.selectedUser = null;
			debugLog(4, "[userSlice][PROCESS] clearSelectedUser", {});
		},
		resetError: (state, action) => {
			// Optional: reset all errors, or just one type based on payload
			if (action.payload) {
				// Reset only one error key, e.g. { type: "create" }
				state.error[action.payload.type] = null;
				debugLog(4, "[userSlice][PROCESS] resetError one", { type: action.payload.type });
			} else {
				// Reset all error keys
				Object.keys(state.error).forEach((key) => {
					state.error[key] = null;
				});
				debugLog(4, "[userSlice][PROCESS] resetError all", {});
			}
		},
	},
	extraReducers: (builder) => {
		builder

			// --- [retrieveUsers] ---
			.addCase(retrieveUsers.pending, (state) => {
				// state.status = "loading";
				// state.error = null;
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(retrieveUsers.fulfilled, (state, action) => {
				// state.status = "succeeded";
				state.status.retrieve = "succeeded";
				state.list = action.payload;
			})
			.addCase(retrieveUsers.rejected, (state, action) => {
				state.status.retrieve = "failed";
				state.error.retrieve = getRejectedError(action, "User retrieve failed.");
				debugLog(4, "[userSlice][PROCESS] retrieveUsers rejected", { error: action.error });
			})

			// --- [retrieveOneUser / retrieveOneUserByUuid] ---
			.addCase(retrieveOneUser.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneUser.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedUser = action.payload;
			})
			.addCase(retrieveOneUser.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "User retrieve failed.", {
					expectNotFound: true,
				});
				debugLog(4, "[userSlice][PROCESS] retrieveOneUser rejected", { error: action.error });
			})

			.addCase(retrieveOneUserByUuid.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneUserByUuid.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedUser = action.payload;
			})
			.addCase(retrieveOneUserByUuid.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "User retrieve failed.", {
					expectNotFound: true,
				});
				debugLog(4, "[userSlice][PROCESS] retrieveOneUserByUuid rejected", { error: action.error });
			})

			// --- [updateUser] ---
			.addCase(updateUser.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updateUser.fulfilled, (state, action) => {
				// Don't modify currentUser if response is just a message
				// state.currentUser = action.payload;
				state.status.update = "succeeded";
				state.error.update = null;

				// Optionally update the list
				const index = state.list.findIndex((u) => u.id === action.payload.id);
				if (index !== -1) state.list[index] = action.payload;

				debugLog(4, "[userSlice][PROCESS] updateUser fulfilled updated user", {
					payload: action.payload,
				});
			})
			.addCase(updateUser.rejected, (state, action) => {
				state.status.update = "failed";
				// Preserve the normalized object so EditUser can show backend validation
				// text without falling back to generic mutation messaging.
				state.error.update = getRejectedError(action, "User update failed.");
				debugLog(4, "[userSlice][PROCESS] updateUser rejected", { error: action.error });
			})

			// --- [createUser] ---
			.addCase(createUser.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createUser.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(createUser.rejected, (state, action) => {
				state.status.create = "failed";
				state.error.create = getRejectedError(action, "User create failed.");
				debugLog(4, "[userSlice][PROCESS] createUser rejected", { error: action.error });
			})

			// --- [findUsersByEmail / Keyword] ---
			.addCase(findUsersByEmail.pending, (state) => {
				state.status.find = "loading";
				state.error.find = null;
			})
			.addCase(findUsersByEmail.fulfilled, (state, action) => {
				state.status.find = "succeeded";
				state.list = action.payload;
			})
			.addCase(findUsersByEmail.rejected, (state, action) => {
				state.status.find = "failed";
				// Search flows should keep the same normalized error object shape as
				// the rest of the module so future search-specific UI can preserve
				// backend messages and transport/timeout behavior consistently.
				state.error.find = getRejectedError(action, "User search failed.");
				debugLog(4, "[userSlice][PROCESS] findUsersByEmail rejected", { error: action.error });
			})

			.addCase(findUsersByKeyword.pending, (state) => {
				state.status.find = "loading";
				state.error.find = null;
			})
			.addCase(findUsersByKeyword.fulfilled, (state, action) => {
				state.status.find = "succeeded";
				state.list = action.payload;
			})
			.addCase(findUsersByKeyword.rejected, (state, action) => {
				state.status.find = "failed";
				state.error.find = getRejectedError(action, "User search failed.");
				debugLog(4, "[userSlice][PROCESS] findUsersByKeyword rejected", { error: action.error });
			})

			.addCase(updateStatus.pending, (state) => {
				state.status.update = "loading"; // or 'updateStatus' if you want it separated
				state.error.update = null;
			})
			.addCase(updateStatus.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				// Status updates only return the identity + new status, so merge the
				// change into existing records instead of replacing full user objects.
				const idx = state.list.findIndex((b) => b.id === action.payload.id);
				if (idx !== -1) state.list[idx] = { ...state.list[idx], ...action.payload };
				if (state.selectedUser && state.selectedUser.id === action.payload.id) {
					state.selectedUser = { ...state.selectedUser, ...action.payload };
				}
			})
			.addCase(updateStatus.rejected, (state, action) => {
				state.status.update = "failed";
				state.error.update = getRejectedError(action, "User status update failed.");
				debugLog(4, "[userSlice][PROCESS] updateStatus rejected", { error: action.error });
			});
	},
});

const { reducer } = userSlice;
export default reducer;

// export const { setCurrentUser } = userSlice.actions;
// updated: export clearCurrentUser along with setCurrentUser
export const { setSelectedUser, clearSelectedUser, resetError } = userSlice.actions;
